<template>
  <details
    v-bind:open="isOpen"
    ref="detailsRef"
    class="shadow-card-soft bg-white rounded-lg border border-gray-150"
  >
    <summary
      class="w-full cursor-pointer flex justify-between gap-3 list-none transition hover:text-blue-600 select-none"
      ref="summaryRef"
      @click.prevent="handleClick"
    >
      <div
        class="max-w-full min-w-0 flex-1 flex items-center gap-3 px-4 md:px-5 lg:px-6 py-3 @xl:py-4"
      >
        <div class="flex-1 min-w-0 flex flex-wrap justify-between items-center gap-y-1.5 gap-x-4">
          <h4 class="w-full min-w-[12rem] flex-1 text-xl leading-tight whitespace-nowrap truncate">
            {{ module.name }}
          </h4>
          <div class="flex flex-wrap shrink items-center gap-2 text-sm">
            <slot name="badges" />
          </div>
        </div>
        <s-icon
          name="chevron-down"
          class="opacity-70 transition-transform duration-200 ease-out"
          :class="{'rotate-180': isOpen}"
        />
      </div>
    </summary>
    <ul
      ref="contentRef"
      class="flex flex-col gap-4 pb-4 md:pb-5 lg:pb-6 px-4 md:px-5 lg:px-6 bg-gradient-to-t from-gray-50 to-transparent rounded-b-lg"
    >
      <slot />
    </ul>
  </details>
</template>

<script setup lang="ts">
import SIcon from '../../../design-system/SIcon.vue';
import SBadge from '../../../design-system/SBadge.vue';
import SLink from '../../../components/SLink.vue';
import {useI18n} from 'vue-i18n';
import {useLocalStorage} from '@vueuse/core';
import {ref} from 'vue';

const {module} = defineProps<{
  module: {
    id: number | string;
    name: string;
  };
}>();

const detailsRef = ref<HTMLDetailsElement | null>(null);
const summaryRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const isOpen = useLocalStorage(`module-card:${module.id}:open`, false);
const isClosing = ref(false);
const isExpanding = ref(false);
const animation = ref<Animation | null>(null);

function handleClick(e: Event) {
  if (!detailsRef.value || !summaryRef.value || !contentRef.value) return;

  detailsRef.value.style.overflow = 'hidden';

  if (isClosing.value || !isOpen.value) {
    open();
  } else if (isExpanding.value || isOpen.value) {
    shrink();
  }
}

function shrink() {
  if (!detailsRef.value || !summaryRef.value) return;

  isClosing.value = true;
  const startHeight = `${detailsRef.value.offsetHeight}px`;
  const endHeight = `${summaryRef.value.offsetHeight}px`;

  if (animation.value) {
    animation.value.cancel();
  }

  animation.value = detailsRef.value.animate(
    {
      height: [startHeight, endHeight],
    },
    {
      duration: 250,
      easing: 'ease-out',
    }
  );

  animation.value.onfinish = () => onAnimationFinish(false);
  animation.value.oncancel = () => (isClosing.value = false);
}

function open() {
  if (!detailsRef.value) return;

  detailsRef.value.style.height = `${detailsRef.value.offsetHeight}px`;
  isOpen.value = true;
  requestAnimationFrame(() => expand());
}

function expand() {
  if (!detailsRef.value || !summaryRef.value || !contentRef.value) return;

  isExpanding.value = true;
  const startHeight = `${detailsRef.value.offsetHeight}px`;
  const endHeight = `${summaryRef.value.offsetHeight + contentRef.value.offsetHeight}px`;

  if (animation.value) {
    animation.value.cancel();
  }

  animation.value = detailsRef.value.animate(
    {
      height: [startHeight, endHeight],
    },
    {
      duration: 250,
      easing: 'ease-out',
    }
  );

  animation.value.onfinish = () => onAnimationFinish(true);
  animation.value.oncancel = () => (isExpanding.value = false);
}

function onAnimationFinish(open: boolean) {
  if (!detailsRef.value) return;

  isOpen.value = open;
  animation.value = null;
  isClosing.value = false;
  isExpanding.value = false;
  detailsRef.value.style.height = '';
  detailsRef.value.style.overflow = '';
}

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});
</script>

<i18n>
{
  "en": {
    "viewModule": "View Module"
  },
  "fr": {
    "viewModule": "Voir le module"
  }
}
</i18n>
